import type { CheckOut } from '@shared/modules/check-outs/check-out';
import { UserPresenter } from '@shared/modules/users/user.presenter';
import { Presenter } from '@shared/presenters/presenter';
import { TIME_FORMAT } from '@shared/utils/date-time';
import type { LatLng } from '@shared/utils/geo';
import { distanceBetween, readableFromMeters } from '@shared/utils/geo';
import type { Duration } from 'luxon';
import { DateTime } from 'luxon';
import { BookingPresenter } from '../bookings/booking.presenter';

export class CheckOutPresenter extends Presenter<CheckOut> {

    get booking(): BookingPresenter {
        return this.present('booking.data', BookingPresenter);
    }

    get manualBy(): UserPresenter {
        return this.present('manual_by.data', UserPresenter);
    }

    get commentsChangedBy(): UserPresenter {
        return this.present('comments_changed_by.data', UserPresenter)
            ?? this.present('comments_changed_activity.data.causer.data', UserPresenter);
    }

    get whenDateTime(): DateTime {
        return this._get('whenDateTime', () => DateTime.fromISO(this.data.when));
    }

    get whenTime() {
        return this.whenDateTime.toFormat(TIME_FORMAT);
    }

    get isToday() {
        return this.whenDateTime.day === DateTime.local().day;
    }

    get whenDateReadable() {
        return this._get('whenDateReadable', () => this.formatDateReadable(this.whenDateTime));
    };

    get punctuality(): Duration {
        return this._get('punctuality', () => this.whenDateTime.diff(this.booking.finish, 'minutes'));
    }

    get punctualityReadable() {
        return this._get('punctualityReadable', () => {
            const minutes = Math.round(Math.abs(this.punctuality.minutes));

            if (minutes === 0) {
                return 'On time';
            }

            return `${minutes}m ${this.punctuality.minutes < 0 ? 'early' : 'late'}`;
        });
    }

    get metresFromBookingAddress() {
        return this._get('metresFromBooking', () => {
            const checkInLatLng = { lat: this.data?.lat, lng: this.data?.lng };
            const bookingLatLng = this.booking?.address?.googleLatLng;
            if (checkInLatLng && bookingLatLng) {
                return distanceBetween(checkInLatLng, bookingLatLng);
            }
        });
    }

    get googleLatLng(): LatLng | null {
        return this._get('googleLatLng', (): LatLng | null => {
            if (this.data.lat && this.data.lng) {
                return { lat: this.data.lat, lng: this.data.lng };
            }

            return null;
        });
    }

    get locationAccuracy() {
        return this._get('locationAccuracy', () => this.data.location_accuracy);
    }

    get locationAccuracyReadable() {
        return this._get('locationAccuracyReadable', () => readableFromMeters(this.data.location_accuracy));
    }

    get locationStaleness(): number {
        return this._get('locationStaleness', () => {
            if (!this.data.location_acquired_at) {
                return;
            }

            const acquired = this.toDateTime('location_acquired_at');

            return this.whenDateTime.diff(acquired, 'seconds').seconds;
        });
    }

    get comments(): string {
        return this.data.comments;
    }
}
